// StyleSettings.js
import { useState, useEffect } from 'react';
import palettes from '../data/palettes.json';
import fonts from '../data/fonts.json';
import './StyleSettings.css';
import { getUserCustomPalettes } from '../services/customPalettes';

const StyleSettings = ({ initialChoices, onChange }) => {
  const [selectedTheme, setSelectedTheme] = useState(initialChoices.theme || 'light');
  const [filteredPalettes, setFilteredPalettes] = useState([]);
  const [styleChoices, setStyleChoices] = useState({
    device: initialChoices.device || 'Desktop', // Keep device but don't display controls
    theme: initialChoices.theme || 'light',
    palette: initialChoices.palette || 'Classic Light',
    font: initialChoices.font || 'Native UI',
    isCustomPalette: initialChoices.isCustomPalette || false,
    customPaletteId: initialChoices.customPaletteId || null
  });
  const [customPalettes, setCustomPalettes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showCustomPalettes, setShowCustomPalettes] = useState(initialChoices.isCustomPalette || false);

  // Fetch custom palettes on component mount
  useEffect(() => {
    const fetchCustomPalettes = async () => {
      setIsLoading(true);
      try {
        const palettes = await getUserCustomPalettes();
        setCustomPalettes(palettes);
        setIsLoading(false);
      } catch (error) {
        console.error('Error fetching custom palettes:', error);
        setIsLoading(false);
      }
    };
    
    fetchCustomPalettes();
  }, []);

  // Update filtered palettes when theme changes
  useEffect(() => {
    // Only update filtered palettes when not showing custom palettes
    if (!showCustomPalettes) {
      setFilteredPalettes(palettes[selectedTheme] || []);
    }
  }, [selectedTheme, showCustomPalettes]);

  // When initialChoices prop changes, update local state
  useEffect(() => {
    setStyleChoices({
      device: initialChoices.device || 'Desktop', // Preserve device setting
      theme: initialChoices.theme || 'light',
      palette: initialChoices.palette || 'Classic Light',
      font: initialChoices.font || 'Native UI',
      isCustomPalette: initialChoices.isCustomPalette || false,
      customPaletteId: initialChoices.customPaletteId || null
    });
    
    setSelectedTheme(initialChoices.theme || 'light');
    setShowCustomPalettes(initialChoices.isCustomPalette || false);
  }, [initialChoices]);

  const handleThemeChange = (theme) => {
    setSelectedTheme(theme);
    setShowCustomPalettes(false); // Switch back to predefined palettes
    
    const defaultPalette = palettes[theme][0].name;
    const newChoices = { 
      ...styleChoices, 
      theme, 
      palette: defaultPalette,
      isCustomPalette: false,
      customPaletteId: null
    };
    setStyleChoices(newChoices);
    onChange(newChoices);
  };

  const handlePaletteChange = (palette) => {
    const newChoices = { 
      ...styleChoices, 
      palette,
      isCustomPalette: false,
      customPaletteId: null
    };
    setStyleChoices(newChoices);
    onChange(newChoices);
  };

  const handleFontChange = (event) => {
    const font = event.target.value;
    const newChoices = { ...styleChoices, font };
    setStyleChoices(newChoices);
    onChange(newChoices);
  };

  const handleShowCustomPalettes = () => {
    setShowCustomPalettes(true);
    
    // If there are custom palettes available, select the first one
    if (customPalettes.length > 0) {
      const firstCustomPalette = customPalettes[0];
      handleCustomPaletteSelect(firstCustomPalette);
    }
  };

  const handleCustomPaletteSelect = (palette) => {
    // Update the theme based on the selected custom palette's theme
    const newChoices = {
      ...styleChoices,
      theme: palette.theme || 'light',
      palette: palette.name,
      isCustomPalette: true,
      customPaletteId: palette._id
    };
    setStyleChoices(newChoices);
    setSelectedTheme(palette.theme || 'light');
    onChange(newChoices);
  };

  return (
    <div className="style-settings-container">
      {/* Warning Banner */}
      <div className="bg-slate-50 border-l-4 border-slate-400 p-4 mb-6">
        <p className="text-sm text-slate-800">
          IMPORTANT: Style changes will only apply to new generations. If you change these settings, next revisions of previously created views may show mixed styles.
        </p>
      </div>

      <div className="settings-inner-container mb-24">
        {/* Font Family Selection - Left Aligned */}
        <div className="mb-6">
          <h2 className="text-lg font-semibold mb-2">Use Font:</h2>
          <select
            value={styleChoices.font}
            onChange={handleFontChange}
            className="w-full px-4 py-2 pr-6 rounded-md bg-gray-200 text-gray-700"
            style={{
              fontFamily: fonts.find((font) => font.name === styleChoices.font)?.family,
            }}
          >
            {fonts.map((font) => (
              <option
                key={font.name}
                value={font.name}
                style={{ fontFamily: font.family }}
              >
                {font.name}
              </option>
            ))}
          </select>
        </div>

        {/* Theme Selection */}
        <div className="mb-6">
          <h2 className="text-lg font-semibold mb-2">Use Theme:</h2>
          <div className="flex space-x-4 mb-4">
            <button
              onClick={() => handleThemeChange('light')}
              className={`px-4 py-2 rounded-md ${
                selectedTheme === 'light' && !showCustomPalettes 
                  ? 'bg-black text-white' 
                  : 'bg-gray-200 text-gray-700'
              }`}
            >
              Light Predefined
            </button>
            <button
              onClick={() => handleThemeChange('dark')}
              className={`px-4 py-2 rounded-md ${
                selectedTheme === 'dark' && !showCustomPalettes 
                  ? 'bg-black text-white' 
                  : 'bg-gray-200 text-gray-700'
              }`}
            >
              Dark Predefined
            </button>
            
            {/* Custom button, only shown if custom palettes exist */}
            {customPalettes.length > 0 && (
              <button
                onClick={handleShowCustomPalettes}
                className={`px-4 py-2 rounded-md ${
                  showCustomPalettes 
                    ? 'bg-black text-white' 
                    : 'bg-gray-200 text-gray-700'
                }`}
              >
                Custom
              </button>
            )}
          </div>
        </div>

        {/* Predefined Palettes - shown when custom palettes are not selected */}
        {!showCustomPalettes && (
          <div className="mb-6">
            <h2 className="text-lg font-semibold mb-2">Color Palette:</h2>
            <div className="palette-container">
              {filteredPalettes.map((palette) => {
                const isSelected = styleChoices.palette === palette.name && !styleChoices.isCustomPalette;
                const isDarkTheme = selectedTheme === 'dark';
                const borderClass = isSelected
                  ? isDarkTheme
                    ? 'border-2 border-pink-500' // Lighter border for selected in dark theme
                    : 'border-2 border-black' // Selected in light theme
                  : isDarkTheme
                    ? 'border border-gray-700' // Darker border for unselected in dark theme
                    : 'border border-gray-300'; // Unselected in light theme
                const textColor = isDarkTheme ? 'text-white' : 'text-black';
                const bgColor = isDarkTheme ? 'bg-gray-800' : 'bg-white';
                return (
                  <div
                    key={palette.name}
                    className={`flex items-center p-2 mb-2 rounded-md cursor-pointer ${borderClass} ${bgColor} ${textColor}`}
                    onClick={() => handlePaletteChange(palette.name)}
                  >
                    <div className="flex space-x-2 mr-4">
                      <div className="w-6 h-6 rounded" style={{ backgroundColor: palette.primary }}></div>
                      <div className="w-6 h-6 rounded" style={{ backgroundColor: palette.secondary }}></div>
                      <div className="w-6 h-6 rounded" style={{ backgroundColor: palette.accent }}></div>
                    </div>
                    <span>{palette.name}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Custom Palettes - shown when "Custom" is selected */}
        {showCustomPalettes && (
          <div className="mb-6">
            <h2 className="text-lg font-semibold mb-2">Custom Palettes:</h2>
            {isLoading ? (
              <div className="flex justify-center py-4">
                <p className="text-gray-500">Loading custom palettes...</p>
              </div>
            ) : customPalettes.length === 0 ? (
              <div className="mt-4 p-4 bg-gray-100 rounded-md text-center">
                <p className="text-gray-500">You haven't created any custom palettes yet.</p>
              </div>
            ) : (
              <div className="palette-container">
              {customPalettes.map((palette) => {
                const isSelected = styleChoices.customPaletteId === palette._id;
                const isLightTheme = palette.theme === 'light';
                
                const borderClass = isSelected
                  ? isLightTheme
                    ? 'border-2 border-black' // Selected in light theme
                    : 'border-2 border-pink-500' // Selected in dark theme
                  : isLightTheme
                    ? 'border border-gray-300' // Unselected in light theme
                    : 'border border-gray-700'; // Unselected in dark theme
                    
                const textColor = isLightTheme ? 'text-black' : 'text-white';
                const bgColor = isLightTheme ? 'bg-white' : 'bg-gray-800';
                  
                return (
                  <div
                    key={palette._id}
                    className={`flex items-center p-2 mb-2 rounded-md cursor-pointer ${borderClass} ${bgColor} ${textColor}`}
                    onClick={() => handleCustomPaletteSelect(palette)}
                  >
                    <div className="flex space-x-2 mr-4">
                      <div className="w-6 h-6 rounded" style={{ backgroundColor: palette.palette.primary }}></div>
                      <div className="w-6 h-6 rounded" style={{ backgroundColor: palette.palette.secondary }}></div>
                      <div className="w-6 h-6 rounded" style={{ backgroundColor: palette.palette.accent }}></div>
                    </div>
                    <span>{isSelected ? <strong>{palette.name}</strong> : palette.name}</span>
                  </div>
                );
              })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default StyleSettings;